import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Building2,
  Calendar,
  GripVertical,
  Mail,
  Phone,
  Tag,
  Users,
  KanbanSquare,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PilotCrmService } from '@/services/pilot-crm.service';
import { EmployerLead, PilotLeadStatus } from '@/models/pilot-crm.model';
import { Badge, Button, Card, LoadingSpinner, SectionHeader } from '@/components/common';
import { LeadQuickActions } from '@/components/pilot-crm/LeadQuickActions';
import { EmailComposeModal } from '@/components/pilot-crm/EmailComposeModal';

const PIPELINE_COLUMNS: { status: PilotLeadStatus; label: string; color: string }[] = [
  { status: 'new', label: 'New', color: 'bg-blue-500' },
  { status: 'invited', label: 'Invited', color: 'bg-purple-500' },
  { status: 'replied', label: 'Replied', color: 'bg-pink-500' },
  { status: 'call_booked', label: 'Call Booked', color: 'bg-yellow-500' },
  { status: 'accepted', label: 'Accepted', color: 'bg-green-500' },
  { status: 'onboarding', label: 'Onboarding', color: 'bg-cyan-500' },
  { status: 'live', label: 'Live', color: 'bg-emerald-500' },
];

const TERMINAL_COLUMNS: { status: PilotLeadStatus; label: string; color: string }[] = [
  { status: 'not_fit', label: 'Not Fit', color: 'bg-red-500' },
  { status: 'paused', label: 'Paused', color: 'bg-gray-500' },
  { status: 'no_response', label: 'No Response', color: 'bg-gray-400' },
];

export const PilotCrmKanban: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [emailModalLead, setEmailModalLead] = useState<EmployerLead | null>(null);
  const [showTerminal, setShowTerminal] = useState(false);

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['pilot-crm-leads'],
    queryFn: () => PilotCrmService.getLeads(),
    enabled: !!profile,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ leadId, status }: { leadId: string; status: PilotLeadStatus }) => {
      if (!profile?.id) throw new Error('Not authenticated');
      await PilotCrmService.updateLead(leadId, { status }, profile.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-leads'] });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const leadsByStatus = useMemo(() => {
    const grouped: Record<PilotLeadStatus, EmployerLead[]> = {} as any;
    [...PIPELINE_COLUMNS, ...TERMINAL_COLUMNS].forEach((col) => {
      grouped[col.status] = [];
    });
    leads.forEach((lead) => {
      if (grouped[lead.status]) {
        grouped[lead.status].push(lead);
      }
    });
    return grouped;
  }, [leads]);

  const activeLead = useMemo(() => {
    if (!activeId) return null;
    return leads.find((lead) => lead.id === activeId) || null;
  }, [activeId, leads]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const leadId = active.id as string;
    const newStatus = over.id as PilotLeadStatus;

    const lead = leads.find((l) => l.id === leadId);
    if (lead && lead.status !== newStatus) {
      updateStatusMutation.mutate({ leadId, status: newStatus });
    }
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading pipeline..." fullScreen />;
  }

  const allColumns = showTerminal ? [...PIPELINE_COLUMNS, ...TERMINAL_COLUMNS] : PIPELINE_COLUMNS;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SectionHeader
          icon={KanbanSquare}
          title="Pipeline Kanban"
          description="Drag and drop leads to update status"
          action={
            <div className="flex items-center gap-3">
              <Button
                variant={showTerminal ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setShowTerminal(!showTerminal)}
              >
                {showTerminal ? 'Hide' : 'Show'} Terminal States
              </Button>
              <Button variant="outline" onClick={() => navigate('/pilot-crm')}>
                List View
              </Button>
              <Button variant="outline" onClick={() => navigate('/pilot-crm/dashboard')}>
                Dashboard
              </Button>
            </div>
          }
        />

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {allColumns.map((column) => (
                <KanbanColumn
                  key={column.status}
                  status={column.status}
                  label={column.label}
                  color={column.color}
                  leads={leadsByStatus[column.status] || []}
                  onLeadClick={(lead) => navigate(`/pilot-crm/leads/${lead.id}`)}
                  onEmailClick={(lead) => setEmailModalLead(lead)}
                />
              ))}
            </div>
          </div>

          <DragOverlay>
            {activeLead ? (
              <LeadCard
                lead={activeLead}
                isDragging
                onClick={() => {}}
                onEmailClick={() => {}}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {emailModalLead && (
        <EmailComposeModal
          lead={emailModalLead}
          onClose={() => setEmailModalLead(null)}
        />
      )}
    </div>
  );
};

interface KanbanColumnProps {
  status: PilotLeadStatus;
  label: string;
  color: string;
  leads: EmployerLead[];
  onLeadClick: (lead: EmployerLead) => void;
  onEmailClick: (lead: EmployerLead) => void;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  status,
  label,
  color,
  leads,
  onLeadClick,
  onEmailClick,
}) => {
  const { setNodeRef } = useSortable({
    id: status,
    data: { type: 'column' },
  });

  return (
    <div
      ref={setNodeRef}
      className="w-72 flex-shrink-0 bg-gray-50 rounded-xl shadow-sm"
    >
      <div className={`${color} text-white px-4 py-3 rounded-t-xl`}>
        <div className="flex items-center justify-between">
          <span className="font-semibold">{label}</span>
          <Badge variant="gray" size="sm" className="bg-white/20 text-white">
            {leads.length}
          </Badge>
        </div>
      </div>

      <SortableContext
        id={status}
        items={leads.map((l) => l.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="p-3 space-y-3 min-h-[200px] max-h-[calc(100vh-300px)] overflow-y-auto">
          {leads.map((lead) => (
            <SortableLeadCard
              key={lead.id}
              lead={lead}
              onClick={() => onLeadClick(lead)}
              onEmailClick={() => onEmailClick(lead)}
            />
          ))}
          {leads.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No leads
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

interface SortableLeadCardProps {
  lead: EmployerLead;
  onClick: () => void;
  onEmailClick: () => void;
}

const SortableLeadCard: React.FC<SortableLeadCardProps> = ({
  lead,
  onClick,
  onEmailClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: lead.id,
    data: { type: 'lead', lead },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <LeadCard
        lead={lead}
        onClick={onClick}
        onEmailClick={onEmailClick}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

interface LeadCardProps {
  lead: EmployerLead;
  onClick: () => void;
  onEmailClick: () => void;
  isDragging?: boolean;
  dragHandleProps?: any;
}

const LeadCard: React.FC<LeadCardProps> = ({
  lead,
  onClick,
  onEmailClick,
  isDragging = false,
  dragHandleProps,
}) => {
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 cursor-pointer hover:shadow-md transition-shadow ${
        isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-2">
        {dragHandleProps && (
          <div
            {...dragHandleProps}
            className="mt-1 cursor-grab active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="font-medium text-gray-900 truncate">{lead.companyName}</span>
          </div>

          {lead.contactName && (
            <p className="text-sm text-gray-600 mt-1 truncate">{lead.contactName}</p>
          )}

          <div className="flex flex-wrap items-center gap-2 mt-2">
            {lead.priority === 'high' && (
              <Badge variant="warning" size="sm">High</Badge>
            )}
            {lead.employeeCountRange && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Users className="w-3 h-3" />
                {lead.employeeCountRange}
              </span>
            )}
          </div>

          {lead.nextActionAt && (
            <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
              <Calendar className="w-3 h-3" />
              Next: {format(lead.nextActionAt, 'MMM d')}
            </div>
          )}

          <div className="flex items-center gap-1 mt-3 pt-2 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
            <button
              className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600"
              onClick={onEmailClick}
              title="Send email"
            >
              <Mail className="w-4 h-4" />
            </button>
            {lead.phone && (
              <a
                href={`tel:${lead.phone}`}
                className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-600"
                title="Call"
              >
                <Phone className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
